import { createApp } from 'vue';
import { DefaultApolloClient } from '@vue/apollo-composable';
import { createPinia } from 'pinia';
import { Dialog, Notify, Quasar, QuasarPluginOptions } from 'quasar';
import iconSetround from 'quasar/icon-set/material-icons-round';
import langDa from 'quasar/lang/da';
import I18n from './i18n';
import './quasar';
import { IstComponents } from '@ist/brdk_uigruppe';
import Axios from 'axios';
import vfmPlugin from 'vue-final-modal';
import App from './App.vue';
import ISTcomponentsIndex from './components/ISTcomponentsIndex';
import { apolloClient } from './graphql/apolloClient';
import { useSecurityStore } from './modules/security/store-index';
import router from './navigation/router';
import { useNavigationStore } from './store';
import './assets/animate.css';
import 'quasar/src/css/index.sass';
import './assets/quasar.scss';
import './assets/ist-style.min.scss';
import './assets/css/output.css';
import VueMatomo from 'vue-matomo';
import { initializeKeycloak } from './auth/authentification';
import { useMainStore } from './store/store';
import CommonUIComponents from '@ist/commonui-components';

const quasarConfig: Partial<QuasarPluginOptions> = {
  lang: langDa,
  config: {
    // @ts-ignore
    brand: {
      primary: '#007e94',
      secondary: '#ff7a00',
      accent: '#00586c',
      positive: '#57d48a',
      negative: '#ff493a',
      info: '#91d1df',
      warning: '#ffd600',
      danger: '#ff493a',
    },
  },
  plugins: { Notify, Dialog },
  iconSet: iconSetround,
};

const app = createApp(App)
  .use(createPinia())
  .use(I18n)
  .use(ISTcomponentsIndex)
  .use(CommonUIComponents)
  .use(Quasar, quasarConfig);

const mainStore = useMainStore();
app.config.performance = true;

// Bootstrap is resilient to a not-yet-ready backend: fetchEnvironment is retried for a bounded window
// before declaring the system unavailable. Without this, a single early failure (e.g. the gateway
// still warming up when the SPA first loads) permanently strands the app on the error page for that
// session — which is exactly what made the e2e landing page flake when it ran before the gateway was up.
const ENVIRONMENT_RETRY_ATTEMPTS = 10;
const ENVIRONMENT_RETRY_DELAY_MS = 2000;

const fetchEnvironmentWithRetry = async () => {
  for (let attempt = 1; ; attempt++) {
    try {
      await mainStore.fetchEnvironment();
      return;
    } catch (error) {
      if (attempt >= ENVIRONMENT_RETRY_ATTEMPTS) throw error;
      await new Promise((resolve) => setTimeout(resolve, ENVIRONMENT_RETRY_DELAY_MS));
    }
  }
};

fetchEnvironmentWithRetry()
  .then(async () => {
    await initializeKeycloak(mainStore.mainConfig);
    app.use(router);

    app.use(vfmPlugin());
    app.use(IstComponents as any);
    app.config.globalProperties.$http = Axios;
    app.config.globalProperties.$router = router;
    app.provide(DefaultApolloClient, apolloClient);
    app.use(VueMatomo, {
      host: 'https://matomo.ist.com',
      siteId: 27,
      router: router,
      enableLinkTracking: true,
      trackPageView: true,
    });
    app.mount('#app');
    useNavigationStore().loadMenu();

    const props = router.resolve({
      name: '',
    });

    useSecurityStore().setLogoutUrl(new URL(props.href, window.location.href || '').href);
  })
  .catch(() => {
    mainStore.setStartupError('backend_unavailable');
    app.use(router);
    app.provide(DefaultApolloClient, apolloClient);
    app.mount('#app');
    router.push({ name: 'error' });
  });
